import React, { useCallback, useState } from 'react';
import { Copy, Check } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { chatMessageToCopyText, copyChatMessageToClipboard } from '@/lib/chatAnswerCopy';
import { useTranslation } from '@/components/utils/translations';

export default function ChatBotMessageExtras({
  message,
  onCandidateSelect,
  onSuggestedNextClick,
  disabled,
}) {
  const { t } = useTranslation();
  const [copied, setCopied] = useState(false);
  const resolved = message?.resolvedAccount;
  const citations = Array.isArray(message?.citations)
    ? message.citations.filter((c) => c && String(c.title || '').trim())
    : [];
  const candidates = Array.isArray(message?.candidates) ? message.candidates : [];
  const suggestedNext = Array.isArray(message?.suggestedNext) ? message.suggestedNext : [];
  const hasCopyContent = Boolean(chatMessageToCopyText(message));
  const showActions = message?.sender === 'bot' && !message?.isStreaming && hasCopyContent;

  const handleCopyAnswer = useCallback(
    (e) => {
      e.preventDefault();
      e.stopPropagation();
      const text = chatMessageToCopyText(message);
      if (!text) return;
      copyChatMessageToClipboard(message).then((ok) => {
        if (!ok) return;
        setCopied(true);
        window.setTimeout(() => setCopied(false), 2000);
      });
    },
    [message],
  );

  return (
    <>
      {showActions ? (
        <div className="mt-2 flex flex-wrap items-center gap-1">
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="h-7 px-2 text-[11px] text-[#5C6B7A] hover:bg-gray-100 hover:text-[#0A1A2F]"
            onClick={handleCopyAnswer}
            onMouseDown={(e) => e.stopPropagation()}
          >
            {copied ? (
              <Check className="h-3.5 w-3.5 mr-1 text-[#00A3A3]" />
            ) : (
              <Copy className="h-3.5 w-3.5 mr-1" />
            )}
            {copied ? t('chatCopied') : t('chatCopy')}
          </Button>
        </div>
      ) : null}

      {resolved?.account_name ? (
        <div className="mt-2">
          <Badge variant="outline" className="text-xs text-[#5C6B7A] font-normal">
            {t('chatTalkingAbout', { account: resolved.account_name })}
          </Badge>
        </div>
      ) : null}

      {citations.length > 0 ? (
        <div className="mt-2 text-xs text-[#5C6B7A] leading-relaxed">
          <span className="font-medium text-[#0A1A2F]">{t('chatSourcesLabel')} </span>
          {citations.map((citation, index) => (
            <span key={`${citation.title}-${index}`}>
              {index > 0 ? ' · ' : ''}
              {citation.url ? (
                <a
                  href={citation.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="underline text-[#00C2C2] hover:text-[#00A3A3]"
                >
                  {citation.title}
                </a>
              ) : (
                citation.title
              )}
            </span>
          ))}
        </div>
      ) : null}

      {message?.intent === 'disambiguation' && candidates.length > 0 ? (
        <div className="mt-3 flex flex-wrap gap-2">
          {candidates.map((candidate) => {
            const name = candidate?.account_name;
            if (!name) return null;
            return (
              <Button
                key={candidate.account_id || name}
                type="button"
                variant="outline"
                size="sm"
                className="text-xs h-8 max-w-full truncate"
                onClick={() => onCandidateSelect?.(name)}
              >
                {name}
              </Button>
            );
          })}
        </div>
      ) : null}

      {!message?.isStreaming && message?.chipNote ? (
        <p className="mt-2 text-xs text-[#5C6B7A]">{message.chipNote}</p>
      ) : null}

      {!message?.isStreaming && suggestedNext.length > 0 ? (
        <div className="mt-3 flex flex-wrap gap-2">
          {suggestedNext.map((item, index) => {
            if (!item || typeof item !== 'object') return null;
            const label = String(item.label || '').trim();
            if (!label) return null;
            const actionType = String(item.actionType ?? item.action_type ?? 'prefill_question').trim() || 'prefill_question';
            if (
              actionType !== 'prefill_question'
              && actionType !== 'navigate'
              && actionType !== 'compose_via_persona'
              && actionType !== 'add_account_to_workspace'
              && actionType !== 'request_account_from_admin'
            ) {
              return null;
            }
            const isAdd = actionType === 'add_account_to_workspace';
            return (
            <Button
              key={`${label}-${index}`}
              type="button"
              variant={isAdd ? 'default' : 'outline'}
              size="sm"
              className={`text-xs h-8 max-w-full truncate ${isAdd ? 'bg-[#00C2C2] hover:bg-[#00A3A3] text-white' : ''}`}
              disabled={disabled || Boolean(message?.chipActionPending)}
              onClick={() => onSuggestedNextClick?.(item, message)}
            >
              {label}
            </Button>
            );
          })}
        </div>
      ) : null}
    </>
  );
}
